/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\OutboundRouteTabs.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import { List, Plus, ArrowRightLeft, GripVertical, Trash2 } from 'lucide-react';
import {
  DndContext, closestCenter, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import SelectGateway from '../../../components/ui/SelectGateway';
import { OutboundRoutePattern, OutboundRouteGateway } from '../types';

// --- PatternRow ------------------------------------------------

interface PatternRowProps {
  index: number;
  pattern: OutboundRoutePattern;
  onUpdate: <K extends keyof OutboundRoutePattern>(index: number, field: K, value: OutboundRoutePattern[K]) => void;
  onRemove: (index: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function PatternRow({ index, pattern, onUpdate, onRemove, t }: PatternRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-border hover:bg-muted/30">
      <td className="text-center py-3 px-2">
        <button type="button" className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </td>
      <td className="py-3 px-4">
        <Input value={pattern.pattern} onChange={(e) => onUpdate(index, 'pattern', e.target.value)} placeholder={t('outboundRoutes.fields.pattern.placeholder')} className="text-sm w-full" />
      </td>
      <td className="text-center py-3 px-4">
        <Input type="number" min="0" value={pattern.stripDigits} onChange={(e) => onUpdate(index, 'stripDigits', parseInt(e.target.value) || 0)} placeholder="0" className="text-sm w-full" />
      </td>
      <td className="py-3 px-4">
        <Input value={pattern.prepend} onChange={(e) => onUpdate(index, 'prepend', e.target.value)} placeholder={t('outboundRoutes.fields.prepend.placeholder')} className="text-sm w-full" />
      </td>
      <td className="text-center py-3 px-4">
        <div className="flex items-center justify-center">
          <Toggle value={pattern.enabled ?? true} onChange={(v) => onUpdate(index, 'enabled', v)} />
          <button type="button" onClick={() => onRemove(index)} className="ml-2 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// --- GatewayRow ------------------------------------------------

interface GatewayRowProps {
  index: number;
  gateway: OutboundRouteGateway;
  availableGateways: Array<{ id: string | number; name: string; description?: string }>;
  onUpdate: <K extends keyof OutboundRouteGateway>(index: number, field: K, value: OutboundRouteGateway[K]) => void;
  onRemove: (index: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  hasError: boolean;
  isLcr: boolean;
}

function GatewayRow({ index, gateway, availableGateways, onUpdate, onRemove, t, hasError, isLcr }: GatewayRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: index });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const isInvalid = !gateway.gatewayId || gateway.gatewayId === 0;

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-border hover:bg-muted/30">
      <td className="text-center py-3 px-2">
        <button type="button" className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </td>
      <td className="py-3 px-4">
        <SelectGateway value={gateway.gatewayId} onChange={(v) => onUpdate(index, 'gatewayId', Number(v))} gateways={availableGateways} placeholder="Select a gateway..." error={hasError && isInvalid} />
      </td>
      <td className="text-center py-3 px-4">
        <Input type="number" min="0" value={gateway.weight} onChange={(e) => onUpdate(index, 'weight', parseInt(e.target.value) || 0)} placeholder="0" className="text-sm w-full" />
      </td>
      {isLcr && (
        <td className="text-center py-3 px-4">
          <Input type="number" min="0" step="0.0001" value={gateway.buyRate || 0} onChange={(e) => onUpdate(index, 'buyRate', parseFloat(e.target.value) || 0)} placeholder="0.0000" className="text-sm w-full" />
        </td>
      )}
      {isLcr && (
        <td className="text-center py-3 px-4">
          <Input type="number" min="0" step="0.0001" value={gateway.sellRate || 0} onChange={(e) => onUpdate(index, 'sellRate', parseFloat(e.target.value) || 0)} placeholder="0.0000" className="text-sm w-full" />
        </td>
      )}
      <td className="text-center py-3 px-4">
        <div className="flex items-center justify-center">
          <Toggle value={gateway.enabled ?? true} onChange={(v) => onUpdate(index, 'enabled', v)} />
          <button type="button" onClick={() => onRemove(index)} className="ml-2 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// --- OutboundRouteTabs -----------------------------------------

interface OutboundRouteTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  patterns: OutboundRoutePattern[];
  gateways: OutboundRouteGateway[];
  availableGateways: Array<{ id: string | number; name: string; description?: string }>;
  errors: Record<string, string>;
  routingStrategy: string;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  onPatternDragEnd: (event: DragEndEvent) => void;
  onGatewayDragEnd: (event: DragEndEvent) => void;
  onUpdatePattern: <K extends keyof OutboundRoutePattern>(index: number, field: K, value: OutboundRoutePattern[K]) => void;
  onRemovePattern: (index: number) => void;
  onAddPattern: () => void;
  onUpdateGateway: <K extends keyof OutboundRouteGateway>(index: number, field: K, value: OutboundRouteGateway[K]) => void;
  onRemoveGateway: (index: number) => void;
  onAddGateway: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export function OutboundRouteTabs({
  activeTab, setActiveTab, patterns, gateways, availableGateways,
  errors, routingStrategy, sensors,
  onPatternDragEnd, onGatewayDragEnd,
  onUpdatePattern, onRemovePattern, onAddPattern,
  onUpdateGateway, onRemoveGateway, onAddGateway,
  t,
}: OutboundRouteTabsProps) {
  const isLcr = routingStrategy === 'lcr';

  return (
    <div className="mt-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 mb-2">
          <TabsTrigger value="patterns">{t('outboundRoutes.tabs.patterns')}</TabsTrigger>
          <TabsTrigger value="gateways">{t('outboundRoutes.tabs.gateways')}</TabsTrigger>
        </TabsList>

        {/* Patterns Tab */}
        <TabsContent value="patterns" className="space-y-3 mt-2">
          <FormBox title={t('outboundRoutes.tabs.patterns')} icon={<List className="h-5 w-5" />}>
            {errors.patterns && <p className="text-sm text-red-500">{errors.patterns}</p>}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPatternDragEnd}>
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-center py-3 px-2 text-sm font-medium text-foreground" style={{ width: '8%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.move.tooltip')}><span>{t('outboundRoutes.fields.move.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '35%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.pattern.tooltip')}><span>{t('outboundRoutes.fields.pattern.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '18%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.stripDigits.tooltip')}><span>{t('outboundRoutes.fields.stripDigits.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '25%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.prepend.tooltip')}><span>{t('outboundRoutes.fields.prepend.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '14%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.enabled.tooltip')}><span>{t('outboundRoutes.fields.enabled.label')}</span></SimpleTooltip>
                    </th>
                  </tr>
                </thead>
                <SortableContext items={patterns.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {patterns.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No patterns added yet. Click "Add Pattern" to create one.</td></tr>
                    ) : (
                      patterns.map((pattern, index) => (
                        <PatternRow key={index} index={index} pattern={pattern} onUpdate={onUpdatePattern} onRemove={onRemovePattern} t={t} />
                      ))
                    )}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
            <div className="mt-4 flex justify-end">
              <Button type="button" onClick={onAddPattern} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />{t('outboundRoutes.actions.addPattern')}
              </Button>
            </div>
          </FormBox>
        </TabsContent>

        {/* Gateways Tab */}
        <TabsContent value="gateways" className="space-y-3 mt-2">
          <FormBox title={t('outboundRoutes.tabs.gateways')} icon={<ArrowRightLeft className="h-5 w-5" />}>
            {errors.gateways && <p className="text-sm text-red-500">{errors.gateways}</p>}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onGatewayDragEnd}>
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-center py-3 px-2 text-sm font-medium text-foreground" style={{ width: '6%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.gatewayMove.tooltip')}><span>{t('outboundRoutes.fields.gatewayMove.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: isLcr ? '30%' : '50%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.gateway.tooltip')}><span>{t('outboundRoutes.fields.gateway.label')}</span></SimpleTooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: isLcr ? '12%' : '20%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.weight.tooltip')}><span>{t('outboundRoutes.fields.weight.label')}</span></SimpleTooltip>
                    </th>
                    {isLcr && (
                      <>
                        <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '14%' }}>
                          <SimpleTooltip content={t('outboundRoutes.fields.buyRate.tooltip')}><span>{t('outboundRoutes.fields.buyRate.label')}</span></SimpleTooltip>
                        </th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '14%' }}>
                          <SimpleTooltip content={t('outboundRoutes.fields.sellRate.tooltip')}><span>{t('outboundRoutes.fields.sellRate.label')}</span></SimpleTooltip>
                        </th>
                      </>
                    )}
                    <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: isLcr ? '18%' : '22%' }}>
                      <SimpleTooltip content={t('outboundRoutes.fields.gatewayEnabled.tooltip')}><span>{t('outboundRoutes.fields.gatewayEnabled.label')}</span></SimpleTooltip>
                    </th>
                  </tr>
                </thead>
                <SortableContext items={gateways.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {gateways.length === 0 ? (
                      <tr><td colSpan={isLcr ? 6 : 4} className="text-center py-8 text-muted-foreground">No gateways assigned yet. Click "Add Gateway" to create one.</td></tr>
                    ) : (
                      gateways.map((gateway, index) => (
                        <GatewayRow key={index} index={index} gateway={gateway} availableGateways={availableGateways} onUpdate={onUpdateGateway} onRemove={onRemoveGateway} t={t} hasError={!!errors.gateways} isLcr={isLcr} />
                      ))
                    )}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
            <div className="mt-4 flex justify-end">
              <Button type="button" onClick={onAddGateway} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />{t('outboundRoutes.actions.addGateway')}
              </Button>
            </div>
          </FormBox>
        </TabsContent>
      </Tabs>
    </div>
  );
}
